import { useState, useMemo, useCallback, useEffect } from 'react';
import { DateTime } from 'luxon';
import { useTheme } from '@material-ui/styles';
import { useMeContext } from '~/modules/me/useMeContext';
import useThemeBreakpoint from '~/modules/common/hooks/useThemeBreakpoint';
import { mapRepliconDateToUtcObject } from '~/modules/common/dates/convert';
import { PERIOD_SCALE_ENUM } from '../periodScale';
import useDateState from './useDateState';
import { getPeriodDateRangeByScale } from './periodDates';

export const scales = [
  PERIOD_SCALE_ENUM.DAYS,
  PERIOD_SCALE_ENUM.WEEKS,
  PERIOD_SCALE_ENUM.MONTHS,
  PERIOD_SCALE_ENUM.QUARTERS,
  PERIOD_SCALE_ENUM.YEARS
];

const calculateOffset = (periodCount, anchor) => {
  if (typeof anchor === 'number') {
    if (anchor <= 0) return 0;
    if (anchor >= 1) return Math.max(0, periodCount - 1);

    return Math.floor((periodCount - 1) * anchor);
  }

  return 0;
};

export const createPeriods = (
  me,
  start,
  scale,
  periodCount,
  anchor,
  isPast,
  centerOffset = false
) => {
  const anchorOffset = calculateOffset(periodCount, anchor);
  const pastAwareStart = isPast
    ? centerOffset
      ? start.minus({ [scale]: periodCount - 5 })
      : start.minus({ [scale]: periodCount - 1 })
    : start;
  const periods = new Array(periodCount).fill({}).map((_, index) => {
    const anchorAdjustedStart = pastAwareStart.plus({
      [scale]: index - anchorOffset
    });

    return getPeriodDateRangeByScale({ me, start: anchorAdjustedStart, scale });
  });

  return periods;
};

const defaultValues = {
  date: DateTime.utc(),
  scale: PERIOD_SCALE_ENUM.MONTHS,
  periodCount: 3
};

const getDefaultDate = date =>
  mapRepliconDateToUtcObject(
    date && isValidDate(date) ? date : defaultValues.date
  );

export const isValidDate = date => date instanceof DateTime && date.isValid;

export const isValidScale = scale => scales.some(option => option === scale);

export const isValidPeriodCount = periodCount =>
  typeof periodCount === 'number' && isFinite(periodCount) && periodCount > 0;

export const useFixedRange = (options = {}) => {
  const {
    getPeriodCount,
    anchor,
    scale: defaultScale,
    date: defaultDate,
    isPast = false,
    centerOffset = false,
    sessionStorageKey
  } = options;
  const theme = useTheme();

  const breakpoint = useThemeBreakpoint(theme);

  const scaleValue =
    defaultScale && isValidScale(defaultScale)
      ? defaultScale
      : defaultValues.scale;

  const [scale, setScale] = useState(scaleValue);

  useEffect(() => setScale(scaleValue), [setScale, scaleValue]);

  const periodCountMemo = useMemo(() => {
    if (getPeriodCount) {
      const periodCount = getPeriodCount(breakpoint, scale);

      if (isValidPeriodCount(periodCount)) return periodCount;
    }

    return defaultValues.periodCount;
  }, [breakpoint, scale, getPeriodCount]);

  const dateMemoized = useMemo(() => getDefaultDate(defaultDate), [
    defaultDate
  ]);

  const [date, setDate] = useDateState({
    initialValue: dateMemoized,
    sessionStorageKey
  });
  const me = useMeContext();

  const { chartDates, dateRange } = useMemo(() => {
    const periods = createPeriods(
      me,
      date,
      scale,
      periodCountMemo,
      anchor,
      isPast,
      centerOffset
    );

    return {
      chartDates: periods,
      dateRange: {
        startDate: periods[0].start,
        endDate: periods[periods.length - 1].end
      }
    };
  }, [me, date, scale, periodCountMemo, anchor, isPast, centerOffset]);

  const handleSetScale = useCallback(
    newValue => {
      if (isValidScale(newValue)) setScale(newValue);
    },
    [setScale]
  );

  const handleSetDate = useCallback(
    newValue => {
      if (isValidDate(newValue)) setDate(newValue);
    },
    [setDate]
  );

  const handlePrevious = useCallback(() => {
    setDate(date.minus({ [`${scale}`]: 1 }));
  }, [date, scale, setDate]);

  const handleNext = useCallback(() => {
    setDate(date.plus({ [`${scale}`]: 1 }));
  }, [date, scale, setDate]);

  const resetDate = useCallback(() => {
    setDate(dateMemoized);
  }, [setDate, dateMemoized]);

  return {
    scale,
    date,
    chartDates,
    dateRange,
    setScale: handleSetScale,
    setDate: handleSetDate,
    onPrevious: handlePrevious,
    onNext: handleNext,
    resetDate
  };
};

export default useFixedRange;
